import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../lib/api";
import { UploadCloud, FileText, Check, Download, Plus, Trash2, Loader2, AlertTriangle, Sparkles, ArrowLeft, Mail, Building2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import ValidatorPanel from "../components/ValidatorPanel";
import SendEmailDialog from "../components/SendEmailDialog";

const FORMATS = [
  { id: "zugferd_en16931", label: "ZUGFeRD 2.5 / Factur-X (EN 16931)", desc: "Standard für B2B, PDF/A-3 mit eingebettetem XML", recommended: true },
  { id: "zugferd_basic", label: "ZUGFeRD 2.5 (BASIC)", desc: "Reduziertes Profil für einfache Rechnungen" },
  { id: "xrechnung", label: "XRechnung 3.0 (reines XML)", desc: "Für öffentliche Auftraggeber (B2G, Deutschland)" },
];

const EU_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
]);
const COUNTRY_NAME_TO_ISO = {
  "österreich":"AT","oesterreich":"AT","austria":"AT",
  "belgien":"BE","belgium":"BE","belgique":"BE","belgië":"BE","belgie":"BE",
  "bulgarien":"BG","bulgaria":"BG",
  "kroatien":"HR","croatia":"HR","hrvatska":"HR",
  "zypern":"CY","cyprus":"CY",
  "tschechien":"CZ","czech republic":"CZ","czechia":"CZ","česko":"CZ","cesko":"CZ",
  "dänemark":"DK","daenemark":"DK","denmark":"DK","danmark":"DK",
  "estland":"EE","estonia":"EE","eesti":"EE",
  "finnland":"FI","finland":"FI","suomi":"FI",
  "frankreich":"FR","france":"FR",
  "deutschland":"DE","germany":"DE",
  "griechenland":"GR","greece":"GR","hellas":"GR",
  "ungarn":"HU","hungary":"HU",
  "irland":"IE","ireland":"IE",
  "italien":"IT","italy":"IT","italia":"IT",
  "lettland":"LV","latvia":"LV","latvija":"LV",
  "litauen":"LT","lithuania":"LT","lietuva":"LT",
  "luxemburg":"LU","luxembourg":"LU",
  "malta":"MT",
  "niederlande":"NL","netherlands":"NL","the netherlands":"NL","holland":"NL","nederland":"NL",
  "polen":"PL","poland":"PL","polska":"PL",
  "portugal":"PT",
  "rumänien":"RO","rumaenien":"RO","romania":"RO","românia":"RO",
  "slowakei":"SK","slovakia":"SK","slovensko":"SK",
  "slowenien":"SI","slovenia":"SI","slovenija":"SI",
  "spanien":"ES","spain":"ES","españa":"ES","espana":"ES",
  "schweden":"SE","sweden":"SE","sverige":"SE",
  "norwegen":"NO","norway":"NO",
  "island":"IS","iceland":"IS",
  "liechtenstein":"LI",
  "schweiz":"CH","switzerland":"CH","suisse":"CH","svizzera":"CH",
  "vereinigtes königreich":"GB","vereinigtes koenigreich":"GB","grossbritannien":"GB","großbritannien":"GB","united kingdom":"GB","great britain":"GB","britain":"GB","uk":"GB","england":"GB",
};
function normalizeCountry(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (!s) return "";
  const key = s.replace(/[.,;:()[\]"'“”„‚’]/g, "").toLowerCase();
  if (COUNTRY_NAME_TO_ISO[key]) return COUNTRY_NAME_TO_ISO[key];
  if (s.length === 2 && /^[a-zA-Z]{2}$/.test(s)) return s.toUpperCase();
  return s;
}

const emptyItem = { name: "", quantity: 1, unit_price: 0, tax_rate: 19, unit_code: "C62" };

function recomputeTotals(items, scenario = "standard") {
  const groups = {};
  let net = 0;
  const forcedZero = scenario !== "standard";
  for (const it of items) {
    const q = Number(it.quantity) || 0;
    const p = Number(it.unit_price) || 0;
    const r = forcedZero ? 0 : (Number(it.tax_rate) || 0);
    const line = q * p;
    net += line;
    groups[r] = (groups[r] || 0) + line;
  }
  const catMap = {
    standard: "S", margin_25a: "E", intra_eu_6a: "K", export_6: "G",
  };
  const reasonMap = {
    margin_25a: "Differenzbesteuerung – Gebrauchtfahrzeug nach § 25a UStG",
    intra_eu_6a: "Steuerfreie innergemeinschaftliche Lieferung gemäß § 6a UStG",
    export_6: "Steuerfreie Ausfuhrlieferung gemäß § 6 UStG",
  };
  const breakdown = Object.entries(groups).map(([rate, basis]) => ({
    rate: Number(rate),
    basis_amount: round2(basis),
    calculated_amount: round2(basis * Number(rate) / 100),
    category_code: catMap[scenario] || "S",
    exemption_reason: reasonMap[scenario] || "",
  }));
  const tax = breakdown.reduce((s, x) => s + x.calculated_amount, 0);
  return { net_total: round2(net), tax_amount: round2(tax), gross_total: round2(net + tax), breakdown };
}
const round2 = (n) => Math.round(Number(n) * 100) / 100;

const TAX_SCENARIOS = [
  { id: "standard", label: "Regelbesteuerung Inland (19 % USt)", short: "19 %", desc: "Standard-Rechnung mit ausgewiesener USt." },
  { id: "margin_25a", label: "Differenzbesteuerung Gebrauchtfahrzeug (§ 25a UStG)", short: "§ 25a", desc: "Händler verkauft Gebrauchtwagen – USt nicht separat ausgewiesen." },
  { id: "intra_eu_6a", label: "Innergemeinschaftliche Lieferung (§ 6a UStG)", short: "§ 6a", desc: "Kunde mit ausländischer EU-USt-IdNr. – steuerfrei." },
  { id: "export_6", label: "Ausfuhrlieferung Drittland (§ 6 UStG)", short: "§ 6", desc: "Lieferung an Käufer außerhalb der EU." },
];

export default function Convert() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=upload, 2=verify, 3=done
  const [dragover, setDragover] = useState(false);
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [err, setErr] = useState("");
  const [extract, setExtract] = useState(null); // { data, pdf_base64, raw_text, filename }
  const [data, setData] = useState(null);
  const [format, setFormat] = useState("zugferd_en16931");
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [profileApplied, setProfileApplied] = useState(false);
  const [dup, setDup] = useState({ exists: false, count: 0, items: [] });
  const inputRef = useRef(null);

  // Warn if an invoice with the same number was converted before (re-check when the number is edited)
  useEffect(() => {
    if (step !== 2 || !data) return;
    const num = (data.invoice_number || "").trim();
    if (!num) { setDup({ exists: false, count: 0, items: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const { data: d } = await api.get("/invoices/check-duplicate", { params: { invoice_number: num } });
        setDup(d);
      } catch (e) { /* warning is optional */ }
    }, 400);
    return () => clearTimeout(t);
  }, [step, data?.invoice_number]);

  const handleFile = async (f) => {
    if (!f || f.type !== "application/pdf") { setErr("Bitte eine PDF-Datei auswählen."); return; }
    setErr(""); setFile(f); setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: resp } = await api.post("/invoices/extract", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setExtract(resp);
      setData(normalizeData(resp.data));
      setProfileApplied(!!resp.profile_applied);
      setDup(resp.duplicate || { exists: false, count: 0, items: [] });
      setStep(2);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragover(false); handleFile(e.dataTransfer.files?.[0]); };

  const updateField = (path, value) => {
    setData((prev) => {
      const n = structuredClone(prev);
      const keys = path.split(".");
      let o = n;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return n;
    });
  };

  const parseNum = (v) => {
    if (typeof v === "number") return v;
    if (v === "" || v == null) return 0;
    const s = String(v).replace(/\s/g, "").replace(",", ".");
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const updateItem = (idx, key, val) => {
    setData((prev) => {
      const n = structuredClone(prev);
      if (key === "name" || key === "unit_code") {
        n.line_items[idx][key] = val;
      } else {
        n.line_items[idx][`${key}_raw`] = val; // keep original string for display
        n.line_items[idx][key] = parseNum(val);
      }
      const t = recomputeTotals(n.line_items, n.tax_scenario || "standard");
      n.totals = { net_total: t.net_total, tax_amount: t.tax_amount, gross_total: t.gross_total };
      n.tax_breakdown = t.breakdown;
      return n;
    });
  };

  const setScenario = (sid) => {
    setData((prev) => {
      const n = structuredClone(prev);
      n.tax_scenario = sid;
      // Normalize line items: force rate=0 for non-standard; reset to 19 if coming back to standard
      const forcedZero = sid !== "standard";
      n.line_items = (n.line_items || []).map((it) => ({
        ...it,
        tax_rate: forcedZero ? 0 : 19,
        tax_rate_raw: forcedZero ? "0" : "19",
      }));
      const t = recomputeTotals(n.line_items, sid);
      n.totals = { net_total: t.net_total, tax_amount: t.tax_amount, gross_total: t.gross_total };
      n.tax_breakdown = t.breakdown;
      const reasons = {
        margin_25a: "Gebrauchtgegenstände / Sonderregelung gem. § 25a UStG",
        intra_eu_6a: "Steuerfreie innergemeinschaftliche Lieferung gem. § 6a UStG",
        export_6: "Steuerfreie Ausfuhrlieferung gem. § 6 UStG",
      };
      n.tax_exemption_reason = reasons[sid] || "";
      return n;
    });
  };

  const addItem = () => setData((p) => ({ ...p, line_items: [...p.line_items, { ...emptyItem, tax_rate: p.tax_scenario !== "standard" ? 0 : 19 }] }));
  const removeItem = (i) => setData((p) => {
    const items = p.line_items.filter((_, k) => k !== i);
    const t = recomputeTotals(items, p.tax_scenario || "standard");
    return { ...p, line_items: items, totals: { net_total: t.net_total, tax_amount: t.tax_amount, gross_total: t.gross_total }, tax_breakdown: t.breakdown };
  });

  const scenarioCheck = (() => {
    if (!data) return { blockers: [], warnings: [] };
    const scen = data.tax_scenario || "standard";
    const blockers = [];
    const warnings = [];
    const buyerCountryIso = normalizeCountry(data.buyer?.country || "");
    const sellerCountryIso = normalizeCountry(data.seller?.country || "DE");
    if (scen === "intra_eu_6a") {
      if (!(data.buyer?.vat_id || "").trim()) {
        blockers.push("Käufer-USt-IdNr. ist für § 6a UStG Pflicht.");
      }
      if (!(data.seller?.vat_id || "").trim()) {
        blockers.push("Verkäufer-USt-IdNr. ist für § 6a UStG Pflicht.");
      }
      if (!EU_COUNTRIES.has(buyerCountryIso)) {
        blockers.push(`Käuferland muss ein EU-Mitgliedsstaat sein (aktuell: ${data.buyer?.country || "—"}).`);
      } else if (buyerCountryIso === sellerCountryIso) {
        blockers.push("Käufer- und Verkäuferland dürfen bei § 6a UStG nicht identisch sein.");
      }
      if (!(data.delivery_date || data.issue_date || "").trim()) {
        blockers.push("Lieferdatum (BT-72) ist für § 6a UStG Pflicht.");
      }
    }
    if (scen === "export_6") {
      if (EU_COUNTRIES.has(buyerCountryIso)) {
        warnings.push(`Hinweis: Käuferland ${buyerCountryIso} liegt in der EU – § 6 UStG gilt nur für Drittländer. Bitte Szenario prüfen.`);
      }
    }
    return { blockers, warnings };
  })();

  const doConvert = async () => {
    setErr(""); setConverting(true);
    try {
      if (scenarioCheck.blockers.length) {
        throw new Error(scenarioCheck.blockers.join(" "));
      }
      const payload = { data, format, pdf_base64: extract.pdf_base64 };
      const resp = await api.post("/invoices/convert", payload, { responseType: "blob" });
      const invId = resp.headers["x-invoice-id"];
      const ext = format === "xrechnung" ? "xml" : "pdf";
      const prefix = format === "xrechnung" ? "XRechnung" : "ZUGFeRD";
      const num = (data.invoice_number || "rechnung").replace(/[^A-Za-z0-9_-]/g, "_");
      const filename = `${prefix}_${num}.${ext}`;
      const url = URL.createObjectURL(resp.data);
      setDownloadInfo({ url, filename, format, invId });
      setStep(3);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setConverting(false);
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setExtract(null); setData(null); setDownloadInfo(null); setErr(""); setDup({ exists: false, count: 0, items: [] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Rechnung konvertieren</h1>
          <p className="text-slate-400 text-sm mt-1">freewarefaktura PDF hochladen → KI prüft Daten → ZUGFeRD / XRechnung herunterladen</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className={`step ${step===1?"active":step>1?"done":""}`} data-testid="step-1">
            <span className="step-num">1</span>PDF Hochladen
          </div>
          <div className={`step ${step===2?"active":step>2?"done":""}`} data-testid="step-2">
            <span className="step-num">2</span>Daten prüfen
          </div>
          <div className={`step ${step===3?"active":""}`} data-testid="step-3">
            <span className="step-num">3</span>Download
          </div>
        </div>
      </div>

      {err && <div className="card border-rose-500/40 bg-rose-500/5 p-4 flex items-start gap-3" data-testid="error-banner">
        <AlertTriangle size={18} className="text-rose-400 shrink-0 mt-0.5"/>
        <div className="text-rose-200 text-sm">{err}</div>
      </div>}

      {step === 1 && (
        <div className="card p-10 fade-in">
          <div
            className={`dropzone ${dragover ? "dragover" : ""}`}
            onDragOver={(e)=>{e.preventDefault(); setDragover(true);}}
            onDragLeave={()=>setDragover(false)}
            onDrop={onDrop}
            onClick={()=>inputRef.current?.click()}
            data-testid="pdf-upload-dropzone"
          >
            <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={(e)=>handleFile(e.target.files?.[0])} data-testid="pdf-file-input"/>
            {extracting ? (
              <div className="flex flex-col items-center gap-4">
                <Loader2 size={48} className="text-blue-400 animate-spin"/>
                <div>
                  <div className="font-display text-xl font-semibold">KI analysiert Ihre Rechnung...</div>
                  <div className="text-slate-400 text-sm mt-1">Rechnungsnummer, Verkäufer, Positionen und Beträge werden erkannt.</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <UploadCloud size={32} className="text-blue-400"/>
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold">freewarefaktura PDF-Rechnung hochladen</div>
                  <div className="text-slate-400 text-sm mt-2">Datei hierher ziehen oder <span className="text-blue-400 underline">durchsuchen</span></div>
                  <div className="text-xs font-mono text-slate-500 mt-3 uppercase tracking-wider">PDF · max. 20 MB · GoBD-sichere Verarbeitung</div>
                </div>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[
              {t:"KI-Erkennung", d:"GPT extrahiert alle Rechnungsdaten automatisch, inkl. Positionen & USt."},
              {t:"Manuelle Freigabe", d:"Alle Felder vor der Konvertierung editierbar – Sie behalten die Kontrolle."},
              {t:"PDF/A-3 Download", d:"ZUGFeRD 2.5 Factur-X mit eingebettetem XML oder XRechnung 3.0."},
            ].map((c)=>(
              <div key={c.t} className="card p-5">
                <div className="font-display font-semibold mb-1">{c.t}</div>
                <div className="text-slate-400 text-sm leading-relaxed">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && data && (
        <div className="grid lg:grid-cols-5 gap-6 fade-in">
          {profileApplied && (
            <div className="lg:col-span-5 card border-blue-500/30 bg-blue-500/5 p-3 flex items-center gap-3" data-testid="profile-applied-banner">
              <Building2 size={16} className="text-blue-300 shrink-0"/>
              <div className="text-sm text-slate-200">Ihre <Link className="text-blue-300 underline" to="/app/profile">Firmen-Stammdaten</Link> wurden automatisch als Verkäufer übernommen.</div>
            </div>
          )}
          {dup.exists && (
            <div className="lg:col-span-5 card border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="duplicate-warning">
              <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5"/>
              <div className="text-sm text-amber-100 space-y-1">
                <div className="font-semibold">Diese Rechnungsnummer gibt es bereits.</div>
                <div>
                  Die Rechnung {(data.invoice_number || "").trim()} wurde schon {dup.count > 1 ? `${dup.count}-mal` : "einmal"} konvertiert
                  {dup.items?.[0]?.created_at ? `, zuletzt am ${new Date(dup.items[0].created_at).toLocaleDateString("de-DE")}` : ""}
                  {dup.items?.[0]?.last_sent_to ? ` und an ${dup.items[0].last_sent_to} gesendet` : ""}.
                  Wenn Sie fortfahren, entsteht ein weiterer Eintrag mit derselben Nummer. Das ist zum Beispiel bei einer Korrektur sinnvoll.
                  Bitte prüfen Sie, ob das so gewollt ist.
                </div>
              </div>
            </div>
          )}
          <div className="lg:col-span-2 card overflow-hidden h-[720px] flex flex-col">
            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <FileText size={14} className="text-slate-400"/>
                <span className="font-mono text-xs text-slate-400 truncate">{extract.filename}</span>
              </div>
              <span className="chip chip-pending" data-testid="status-chip-pending"><Sparkles size={12}/>KI-Entwurf</span>
            </div>
            <iframe
              title="pdf-preview"
              src={`data:application/pdf;base64,${extract.pdf_base64}#toolbar=0&navpanes=0`}
              className="flex-1 w-full bg-slate-950"
              data-testid="pdf-preview-iframe"
            />
          </div>

          <div className="lg:col-span-3 card p-6 space-y-6 max-h-[720px] overflow-y-auto">
            <Section title="Steuer-Szenario">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tax-scenario-picker">
                {TAX_SCENARIOS.map((s) => (
                  <label key={s.id} className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition ${data.tax_scenario===s.id ? "border-amber-500/60 bg-amber-500/5" : "border-slate-800 hover:border-slate-700"}`} data-testid={`tax-scenario-${s.id}`}>
                    <input type="radio" name="tax-scenario" checked={data.tax_scenario===s.id} onChange={()=>setScenario(s.id)} className="mt-1 accent-amber-500" data-testid={`tax-scenario-${s.id}-radio`}/>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                        <span>{s.label}</span>
                        <span className="chip chip-draft">{s.short}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{s.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {data.tax_scenario && data.tax_scenario !== "standard" && (
                <div className="mt-3 bg-amber-500/5 border border-amber-500/30 rounded-xl p-3" data-testid="exemption-reason-wrap">
                  <label className="label">Hinweistext in der Rechnung (BT-120 ExemptionReason)</label>
                  <textarea rows={2} className="textarea text-sm" value={data.tax_exemption_reason||""} onChange={(e)=>updateField("tax_exemption_reason", e.target.value)} data-testid="exemption-reason-input"/>
                  <div className="text-[11px] text-amber-200/80 mt-1">Alle Positionen werden automatisch auf 0 % gesetzt. Dieser Text erscheint im XML und auf der E-Rechnung.</div>
                </div>
              )}
            </Section>

            <Section title="Rechnungsdaten">
              <Grid cols={3}>
                <div>
                  <label className="label">Dokumentart</label>
                  <select className="input" value={data.type_code||"380"} onChange={(e)=>updateField("type_code", e.target.value)} data-testid="type-code-select">
                    <option value="380">Rechnung (380)</option>
                    <option value="381">Gutschrift / Rechnungskorrektur (381)</option>
                  </select>
                </div>
                {data.type_code === "381" && (
                  <Field label="Originalrechnung (Nr.)" tid="preceding-invoice-input" value={data.preceding_invoice_number||""} onChange={(v)=>updateField("preceding_invoice_number", v)}/>
                )}
                <Field label="Rechnungsnummer" tid="invoice-number-input" value={data.invoice_number} onChange={(v)=>updateField("invoice_number", v)}/>
                <Field label="Rechnungsdatum" tid="issue-date-input" value={data.issue_date} onChange={(v)=>updateField("issue_date", v)} placeholder="DD.MM.YYYY"/>
                <Field label="Fälligkeitsdatum" tid="due-date-input" value={data.due_date||""} onChange={(v)=>updateField("due_date", v)} placeholder="DD.MM.YYYY"/>
                <Field label="Kundennummer / Leitweg-ID" tid="buyer-ref-input" value={data.buyer_reference||""} onChange={(v)=>updateField("buyer_reference", v)}/>
                <Field label="Währung" tid="currency-input" value={data.currency} onChange={(v)=>updateField("currency", v)}/>
                <Field label="Zahlungsbedingung" tid="payment-terms-input" value={data.payment_terms||""} onChange={(v)=>updateField("payment_terms", v)}/>
              </Grid>
            </Section>

            <PartySection title="Verkäufer (Werkstatt / Händler)" data={data.seller} prefix="seller" updateField={updateField}/>
            <PartySection title="Käufer (Kunde)" data={data.buyer} prefix="buyer" updateField={updateField} isBuyer/>

            <Section title={`Positionen (${data.line_items.length})`} action={
              <button className="btn-ghost text-xs" onClick={addItem} data-testid="add-line-item-button"><Plus size={14}/>Position</button>
            }>
              <div className="space-y-3">
                {data.line_items.map((it, i)=>(
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-900/50 border border-slate-800 rounded-xl p-3" data-testid={`line-item-row-${i}`}>
                    <div className="col-span-12 sm:col-span-5">
                      <label className="label">Beschreibung</label>
                      <input className="input" value={it.name} onChange={(e)=>updateItem(i,"name",e.target.value)} data-testid={`line-item-name-${i}`}/>
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <label className="label">Menge</label>
                      <input type="text" inputMode="decimal" className="input" value={it.quantity_raw ?? it.quantity} onChange={(e)=>updateItem(i,"quantity",e.target.value)} data-testid={`line-item-qty-${i}`}/>
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <label className="label">Einzelpreis</label>
                      <input type="text" inputMode="decimal" className="input" value={it.unit_price_raw ?? it.unit_price} onChange={(e)=>updateItem(i,"unit_price",e.target.value)} data-testid={`line-item-price-${i}`}/>
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <label className="label">USt %</label>
                      <input type="text" inputMode="decimal" className="input" value={it.tax_rate_raw ?? it.tax_rate} onChange={(e)=>updateItem(i,"tax_rate",e.target.value)} data-testid={`line-item-tax-${i}`} disabled title={data.tax_scenario === "standard" ? "Regelbesteuerung – fest 19 %" : "Bei Steuer-Befreiung immer 0 %"}/>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button className="btn-danger p-2" onClick={()=>removeItem(i)} data-testid={`line-item-remove-${i}`}><Trash2 size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Summen">
              <div className="grid grid-cols-3 gap-3 bg-slate-900/70 border border-slate-800 rounded-xl p-4 font-mono text-sm">
                <div><div className="text-slate-400 text-xs mb-1">Netto</div><div className="text-lg text-slate-100" data-testid="total-net">{fmt(data.totals.net_total)} {data.currency}</div></div>
                <div><div className="text-slate-400 text-xs mb-1">USt</div><div className="text-lg text-amber-300" data-testid="total-tax">{fmt(data.totals.tax_amount)} {data.currency}</div></div>
                <div><div className="text-slate-400 text-xs mb-1">Brutto</div><div className="text-lg text-emerald-300 font-bold" data-testid="total-gross">{fmt(data.totals.gross_total)} {data.currency}</div></div>
              </div>
            </Section>

            <Section title="E-Rechnungs-Format">
              <div className="space-y-2">
                {FORMATS.map(f => (
                  <label key={f.id} className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition ${format===f.id ? "border-blue-500/60 bg-blue-500/5" : "border-slate-800 hover:border-slate-700"}`} data-testid={`format-${f.id}-option`}>
                    <input type="radio" name="format" checked={format===f.id} onChange={()=>setFormat(f.id)} className="mt-1 accent-blue-500" data-testid={`format-${f.id}-radio`}/>
                    <div>
                      <div className="font-medium text-slate-100 flex items-center gap-2">{f.label}{f.recommended && <span className="chip chip-valid">Empfohlen</span>}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{f.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Section>

            <ValidatorPanel data={data} format={format}/>

            {(scenarioCheck.blockers.length > 0 || scenarioCheck.warnings.length > 0) && (
              <div className="space-y-2" data-testid="scenario-check-panel">
                {scenarioCheck.blockers.map((b, i) => (
                  <div key={`b-${i}`} className="card border-rose-500/40 bg-rose-500/5 p-3 flex items-start gap-2 text-sm" data-testid={`scenario-blocker-${i}`}>
                    <AlertTriangle size={16} className="text-rose-400 shrink-0 mt-0.5"/>
                    <div className="text-rose-200">{b}</div>
                  </div>
                ))}
                {scenarioCheck.warnings.map((w, i) => (
                  <div key={`w-${i}`} className="card border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2 text-sm" data-testid={`scenario-warning-${i}`}>
                    <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5"/>
                    <div className="text-amber-200">{w}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <button className="btn-ghost" onClick={reset} data-testid="back-to-upload-button"><ArrowLeft size={14}/>Andere Datei</button>
              <button className="btn-amber" onClick={doConvert} disabled={converting || scenarioCheck.blockers.length > 0} data-testid="convert-and-download-button" title={scenarioCheck.blockers.length ? "Bitte erst die rot markierten Pflichtfelder korrigieren." : ""}>
                {converting ? <span className="spinner"/> : <Check size={16}/>}
                {converting ? "Erstelle E-Rechnung..." : (dup.exists ? "Trotzdem konvertieren & herunterladen" : "Konvertieren & Herunterladen")}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && downloadInfo && (
        <div className="card p-10 text-center fade-in">
          <div className="w-20 h-20 rounded-3xl bg-emerald-500/10 border border-emerald-500/30 mx-auto flex items-center justify-center mb-5">
            <Check size={38} className="text-emerald-400"/>
          </div>
          <h2 className="font-display text-3xl font-bold mb-2" data-testid="success-title">E-Rechnung erfolgreich erstellt</h2>
          <p className="text-slate-400 mb-6">Ihre Rechnung wurde {downloadInfo.format === "xrechnung" ? "als XRechnung 3.0 (XML)" : "als ZUGFeRD / Factur-X PDF/A-3 mit eingebettetem XML"} generiert und in der Historie gespeichert.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a href={downloadInfo.url} download={downloadInfo.filename} className="btn-amber" data-testid="download-result-button">
              <Download size={16}/> {downloadInfo.filename}
            </a>
            <button className="btn-primary" onClick={()=>setEmailOpen(true)} data-testid="send-email-button">
              <Mail size={16}/> Per E-Mail senden
            </button>
            <button className="btn-ghost" onClick={reset} data-testid="new-conversion-button">Weitere Rechnung</button>
            <button className="btn-ghost" onClick={()=>nav("/app/dashboard")} data-testid="go-to-history-button">Zur Historie</button>
          </div>
          {emailOpen && downloadInfo.invId && (
            <SendEmailDialog
              invoiceId={downloadInfo.invId}
              defaults={{ recipient_name: data?.buyer?.name || "" }}
              onClose={()=>setEmailOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function fmt(n){ return Number(n).toLocaleString("de-DE", {minimumFractionDigits:2, maximumFractionDigits:2});}

function normalizeData(d) {
  const seller = { name:"",address_line:"",postcode:"",city:"",country:"DE",vat_id:"",tax_number:"",iban:"",bic:"", ...(d.seller||{}) };
  const buyer = { name:"",address_line:"",postcode:"",city:"",country:"DE",vat_id:"",email:"", ...(d.buyer||{}) };
  const scenario = d.tax_scenario || "standard";
  const forcedZero = scenario !== "standard";
  const items = (d.line_items||[]).map(it => ({
    name: it.name||"", quantity: Number(it.quantity||1), unit_price: Number(it.unit_price||0),
    tax_rate: forcedZero ? 0 : Number(it.tax_rate||19),
    unit_code: it.unit_code||"C62",
    tax_category_code: it.tax_category_code || null,
  }));
  const t = recomputeTotals(items, scenario);
  return {
    invoice_number: d.invoice_number||"",
    issue_date: d.issue_date||"",
    due_date: d.due_date||"",
    delivery_date: d.delivery_date||"",
    currency: d.currency||"EUR",
    buyer_reference: d.buyer_reference||"",
    payment_terms: d.payment_terms||"Überweisung",
    tax_scenario: scenario,
    tax_exemption_reason: d.tax_exemption_reason || "",
    seller, buyer, line_items: items,
    tax_breakdown: t.breakdown,
    totals: { net_total: t.net_total, tax_amount: t.tax_amount, gross_total: t.gross_total },
    notes: d.notes||"",
    type_code: d.type_code === "381" ? "381" : "380",
    preceding_invoice_number: d.preceding_invoice_number||"",
  };
}

const Section = ({title, children, action}) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-slate-100">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);
const Grid = ({cols=2, children}) => (<div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-3`}>{children}</div>);
const Field = ({label, value, onChange, placeholder, tid}) => (
  <div><label className="label">{label}</label><input className="input" value={value||""} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} data-testid={tid}/></div>
);

function PartySection({title, data, prefix, updateField, isBuyer=false}) {
  return (
    <Section title={title}>
      <Grid cols={2}>
        <Field label="Name / Firma" tid={`${prefix}-name-input`} value={data.name} onChange={(v)=>updateField(`${prefix}.name`, v)}/>
        <Field label="Straße + Nr." tid={`${prefix}-address-input`} value={data.address_line} onChange={(v)=>updateField(`${prefix}.address_line`, v)}/>
        <Field label="PLZ" tid={`${prefix}-postcode-input`} value={data.postcode} onChange={(v)=>updateField(`${prefix}.postcode`, v)}/>
        <Field label="Stadt" tid={`${prefix}-city-input`} value={data.city} onChange={(v)=>updateField(`${prefix}.city`, v)}/>
        <Field label="Land (ISO)" tid={`${prefix}-country-input`} value={data.country} onChange={(v)=>updateField(`${prefix}.country`, v)}/>
        <Field label="USt-IdNr." tid={`${prefix}-vatid-input`} value={data.vat_id} onChange={(v)=>updateField(`${prefix}.vat_id`, v)}/>
        {isBuyer && (
          <Field label="E-Mail (für XRechnung)" tid={`${prefix}-email-input`} value={data.email||""} onChange={(v)=>updateField(`${prefix}.email`, v)}/>
        )}
        {!isBuyer && (<>
          <Field label="Steuernummer" tid={`${prefix}-taxnum-input`} value={data.tax_number||""} onChange={(v)=>updateField(`${prefix}.tax_number`, v)}/>
          <Field label="IBAN" tid={`${prefix}-iban-input`} value={data.iban||""} onChange={(v)=>updateField(`${prefix}.iban`, v)}/>
          <Field label="BIC" tid={`${prefix}-bic-input`} value={data.bic||""} onChange={(v)=>updateField(`${prefix}.bic`, v)}/>
        </>)}
      </Grid>
    </Section>
  );
}
